import { useState, useCallback } from 'react';
import { GradoFormState, GRADO_FORM_INITIAL, GradoPayload, NivelEducativo } from '../types/grado.types';
import { gradoService } from '../services/gradoService';

// ============================================================
// useGradoForm – creación y edición de grados
// ============================================================

export interface GradoFormErrors { [field: string]: string }

export const useGradoForm = (idGrado: number | null, onSuccess: () => void) => {
  const isEditing = idGrado !== null;

  const [form, setForm]               = useState<GradoFormState>(GRADO_FORM_INITIAL);
  const [errors, setErrors]           = useState<GradoFormErrors>({});
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loadingInit, setLoadingInit] = useState(false);

  // ── Cargar datos en modo edición ──────────────────────────
  const loadGrado = useCallback(async () => {
    if (!isEditing || idGrado === null) return;
    setLoadingInit(true);
    try {
      const data = await gradoService.obtener(idGrado);
      setForm({
        nombre: data.nombre,
        nivel:  data.nivel as NivelEducativo,
        activo: data.activo,
      });
    } catch { /* mantiene vacío */ }
    finally { setLoadingInit(false); }
  }, [idGrado, isEditing]);

  // ── Cambio de campo ───────────────────────────────────────
  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }, []);

  // ── Validación ────────────────────────────────────────────
  const validate = (): GradoFormErrors => {
    const e: GradoFormErrors = {};
    if (!form.nombre.trim()) e.nombre = 'El nombre es requerido';
    if (!form.nivel)         e.nivel  = 'El nivel es requerido';
    return e;
  };

  // ── Submit ────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: GradoPayload = {
        nombre: form.nombre.trim(),
        nivel:  form.nivel,
        activo: form.activo,
      };
      if (isEditing && idGrado) {
        await gradoService.modificar(idGrado, payload);
      } else {
        await gradoService.crear(payload);
      }
      onSuccess();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar el grado');
    } finally {
      setSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isEditing, idGrado]);

  return {
    form, errors, submitting, submitError, loadingInit,
    handleChange, handleSubmit, loadGrado,
  };
};
